import { Link, Navigate, useNavigate } from "react-router-dom";

import { ErrorNote } from "@/components/AppShell";
import HouseholdShell from "@/components/HouseholdShell";
import { globalApi } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import type { VesselSummary } from "@/types";

const TYPE_LABEL: Record<VesselSummary["vessel_type"], string> = {
  pool: "Pool",
  hot_tub: "Hot tub",
};

/**
 * Which body of water are you here about?
 *
 * **Skipped entirely when there is only one.** For the pool this app was built
 * for, a landing page offering a single choice is a tap added to every visit to
 * solve a problem that owner does not have — so one vessel redirects straight
 * through, and the picker only ever appears once a second exists. `replace` so
 * the back button leaves the app rather than bouncing off the redirect.
 */
export default function VesselPicker() {
  const navigate = useNavigate();
  const vessels = useAsync<VesselSummary[]>(
    () => globalApi.get<VesselSummary[]>("/api/vessels"),
    [],
  );

  if (vessels.error) {
    return (
      <HouseholdShell title="Which one?">
        <ErrorNote message={vessels.error} />
      </HouseholdShell>
    );
  }

  if (!vessels.data) return null;
  // Straight through when there is only one — a landing page offering a single
  // choice is a tap added to every visit. `Add` is still reachable from More,
  // which is where someone with one pool would look for it anyway.
  if (vessels.data.length === 1) {
    return <Navigate to={`/v/${vessels.data[0].id}`} replace />;
  }

  return (
    <HouseholdShell
      title="Which one?"
      subtitle="Readings, doses, history and settings all belong to whichever you pick."
    >
      <ul className="space-y-2">
        {vessels.data.map((vessel) => (
          <li key={vessel.id}>
            <button
              type="button"
              onClick={() => navigate(`/v/${vessel.id}`)}
              className="card flex w-full items-baseline justify-between gap-3 p-4 text-left"
            >
              <span>
                <span className="block text-sm font-medium">{vessel.name}</span>
                <span className="unit mt-0.5 block">{TYPE_LABEL[vessel.vessel_type]}</span>
              </span>
              <span className="unit shrink-0">
                <span className="figure">{Number(vessel.volume_gallons).toLocaleString()}</span> gal
              </span>
            </button>
          </li>
        ))}
      </ul>

      <Link to="/add" className="btn btn-quiet mt-3 w-full text-sm">
        Add a pool or hot tub
      </Link>
    </HouseholdShell>
  );
}
